import { Bot, Check, LockKeyhole, Send, Sparkles, TrendingUp } from 'lucide-react'
import { useMemo, useState, type FormEvent } from 'react'
import { useAuth } from '../../auth/AuthGate'
import { saveCloudState, toCloudState } from '../../data/cloud'
import { useStrata } from '../../data/StrataProvider'
import type { CoachMessage, CoachProposal } from '../../domain/types'

const starters = [
  'Revisa mis últimas sesiones y dime qué progresar esta semana.',
  'Ajusta pesos, repeticiones y descansos sin cambiar el objetivo de mi rutina.',
  '¿Dónde estoy estancado según mis registros?',
]

export function CoachPage() {
  const { user } = useAuth()
  const { state, addCoachMessage, applyCoachProposal } = useStrata()
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [applied, setApplied] = useState<string[]>([])
  const messages = useMemo(() => state.coachMessages.slice(-30), [state.coachMessages])

  const send = async (event?: FormEvent, starter?: string) => {
    event?.preventDefault()
    const content = (starter ?? draft).trim()
    if (!content || sending) return
    setDraft('')
    setSending(true)
    const userMessage: CoachMessage = { id: crypto.randomUUID(), role: 'user', content, createdAt: new Date().toISOString() }
    addCoachMessage(userMessage)
    try {
      await saveCloudState(toCloudState(state)).catch(() => undefined)
      const response = await fetch('/api/coach/chat', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: content }),
      })
      const result = await response.json() as { reply?: string; proposal?: CoachProposal; error?: string }
      if (!response.ok || !result.reply) throw new Error(result.error || `coach_${response.status}`)
      addCoachMessage({ id: crypto.randomUUID(), role: 'assistant', content: result.reply, proposal: result.proposal, createdAt: new Date().toISOString() })
    } catch (error) {
      const unavailable = error instanceof Error && ['coach_not_configured', 'provider_unavailable', 'provider_error'].includes(error.message)
      addCoachMessage({
        id: crypto.randomUUID(),
        role: 'assistant',
        content: unavailable ? 'El Coach no pudo conectar con DeepSeek ahora mismo. Tus registros siguen guardados y puedes volver a intentarlo.' : 'No pude responder en este momento. Revisa tu conexión e inténtalo otra vez.',
        createdAt: new Date().toISOString(),
      })
    } finally {
      setSending(false)
    }
  }

  const apply = (message: CoachMessage) => {
    if (!message.proposal || applied.includes(message.id)) return
    applyCoachProposal(message.proposal)
    setApplied((current) => [...current, message.id])
  }

  return (
    <div className="page coach-page">
      <header className="page-header coach-header">
        <span className="eyebrow">ENTRENADOR PERSONAL</span>
        <div className="title-row"><h1>Coach IA</h1><span className="coach-live"><Sparkles size={14} /> DeepSeek</span></div>
        <p>Lee tus datos de entrenamiento, propone cambios y sólo los aplica cuando tú aceptas.</p>
      </header>

      <div className="account-isolation"><LockKeyhole size={16} /><span><strong>Espacio privado de {user.email}</strong> Ninguna conversación, rutina o registro se mezcla con otra cuenta.</span></div>

      {!messages.length && (
        <section className="coach-empty">
          <div><Bot size={28} /></div><h2>Ya conozco tu contexto guardado.</h2>
          <p>Puedo revisar peso, macros, adherencia, sesiones, series, RIR y tu rutina actual. Nunca cambio nada sin mostrarte una propuesta.</p>
          <div className="starter-prompts">{starters.map((starter) => <button key={starter} onClick={() => void send(undefined, starter)}>{starter}<Send size={14} /></button>)}</div>
        </section>
      )}

      <section className="coach-thread" aria-live="polite">
        {messages.map((message) => (
          <article key={message.id} className={`coach-message ${message.role}`}>
            <span>{message.role === 'assistant' ? 'COACH' : 'TÚ'}</span>
            <p>{message.content}</p>
            {message.proposal && (
              <div className="coach-proposal">
                <div><TrendingUp size={18} /><span><strong>{message.proposal.title}</strong><small>{message.proposal.explanation ?? proposalSummary(message.proposal)}</small></span></div>
                <button disabled={applied.includes(message.id)} onClick={() => apply(message)}>{applied.includes(message.id) ? <><Check size={16} /> Aplicado</> : 'Revisé · aplicar ajustes'}</button>
              </div>
            )}
          </article>
        ))}
        {sending && <article className="coach-message assistant thinking"><span>COACH</span><p>Revisando tus registros…</p></article>}
      </section>

      <form className="coach-composer" onSubmit={(event) => void send(event)}>
        <textarea maxLength={4000} rows={2} value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Pregunta o pide un ajuste…" />
        <button disabled={!draft.trim() || sending} aria-label="Enviar al Coach"><Send size={18} /></button>
      </form>
      <p className="coach-vision-note">DeepSeek analiza texto y registros, no fotografías. Tus fotos permanecen locales; para evaluación visual real hará falta conectar un modelo con visión explícitamente.</p>
    </div>
  )
}

function proposalSummary(proposal: CoachProposal) {
  const exercises = proposal.exerciseAdjustments?.length ?? 0
  const routine = proposal.routineAdjustments?.length ?? 0
  return `${exercises + routine} ${exercises + routine === 1 ? 'ajuste preparado' : 'ajustes preparados'}`
}
